import { useRef, useState } from 'react';
import { Upload, X, Loader2, ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiMethods } from '@/services/api';

// Small logo picker: uploads to /api/upload/org-logo (square-ish, transparent PNG/SVG-rasterized friendly)
// and hands the resulting "/uploads/org-logos/..." URL to onChange. The caller persists it.
const LogoUpload = ({ value, onChange, label = 'Logo', size = 'md', disabled = false }) => {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const box = size === 'sm' ? 'h-10 w-10' : 'h-12 w-12';

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type)) {
            toast.error('Please choose a PNG, JPG, GIF or WebP image');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Logo must be less than 5MB');
            return;
        }
        const fd = new FormData();
        fd.append('image', file);
        setUploading(true);
        try {
            const res = await apiMethods.upload.uploadOrgLogo(fd);
            if (res.data?.url) await onChange(res.data.url);
        } catch {
            // api interceptor shows the server message
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="flex items-center gap-3">
            <div
                className={`${box} flex flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-white`}
                title={label}
            >
                {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                ) : value ? (
                    <img src={value} alt={label} className="h-full w-full object-contain p-0.5" />
                ) : (
                    <ImageIcon className="h-5 w-5 text-gray-300" />
                )}
            </div>
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={onFile} />
            <button
                type="button"
                disabled={uploading || disabled}
                onClick={() => inputRef.current?.click()}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
                <Upload className="mr-1.5 h-4 w-4" />
                {value ? 'Change' : `Upload ${label.toLowerCase()}`}
            </button>
            {value && !uploading && (
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange('')}
                    className="inline-flex items-center text-sm text-gray-500 hover:text-red-600"
                    aria-label={`Remove ${label.toLowerCase()}`}
                >
                    <X className="mr-0.5 h-4 w-4" />
                    Remove
                </button>
            )}
        </div>
    );
};

export default LogoUpload;
